import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { ITask, ITasksExecutionFlow } from '~/types/app-setup'

import {
  createAppSetup,
  createTask,
  deleteTask,
  editTask,
  executeTask,
  getAppSetup,
  getTasks,
} from '~/api/application-setup'

////////// KEYS //////////

export const keys = {
  all: ['applications-setup'] as const,
  get: ({ appKey, deviceType }: { appKey?: string; deviceType?: string }) =>
    [...keys.all, 'get', appKey, deviceType] as const,
  list: () => [...keys.all, 'list'] as const,
}

////////// QUERIES //////////

export const useAppSetupQuery = ({
  appKey,
  deviceType,
  pollingEnabled = true,
}: {
  appKey?: string
  deviceType?: string
  pollingEnabled?: boolean
}): UseQueryResult<ITasksExecutionFlow, unknown> => {
  return useQuery({
    queryFn: appKey && deviceType ? () => getAppSetup(appKey, deviceType) : skipToken,
    queryKey: keys.get({ appKey, deviceType }),
    refetchInterval: pollingEnabled ? 5000 : false,
  })
}

export const useAppSetupTaksQuery = (): UseQueryResult<ITask[], unknown> => {
  return useQuery({
    queryFn: getTasks,
    queryKey: keys.list(),
  })
}

//////// MUTATIONS //////////

export const useCreateAppSetupMutation = (): UseMutationResult<
  string[],
  unknown,
  { appKey: string; deviceType: string; taskIds: string[] }
> => {
  return useMutation({ mutationFn: createAppSetup })
}

export const useExecuteTaskMutation = (): UseMutationResult<
  void,
  unknown,
  { appKey: string; deviceType: string; taskId: string },
  unknown
> => {
  return useMutation({ mutationFn: executeTask })
}

export const useCreateTaskMutation = (): UseMutationResult<
  ITask,
  unknown,
  Omit<ITask, 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>,
  unknown
> => {
  return useMutation({ mutationFn: createTask })
}

export const useDeleteTaskMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: deleteTask })
}

export const useUpdateTaskMutation = (): UseMutationResult<
  ITask,
  unknown,
  {
    payload: Omit<ITask, 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>
  },
  unknown
> => {
  return useMutation({ mutationFn: editTask })
}
